// ============================================================
// KOT Enterprises — Corporate Companies & Strategic Programs
// Source: KOT Corporate Statutes v2.0 (Articles 18–19, Parts III–XIII)
// Har entity ki "site" CompanyPage template se render hoti hai.
// ============================================================

export interface EntityService {
  title: string;
  desc: string;
}

export interface Entity {
  slug: string;
  type: "company" | "program";
  name: string;
  tagline: string;
  headline: string;
  summary: string;
  purpose: string[];
  services: EntityService[];
  ecosystemRole: string;
  /** Optional: "Why {Company}?" section ke capability cards (uniform grid) */
  capabilities?: EntityService[];
  /** Capabilities section ka custom title (default: "Our Capabilities") */
  capabilitiesTitle?: string;
  /** Optional: capabilities ke baad detail sections (08-15 style) */
  details?: {
    /** Statement-style section sab se pehle (e.g. "Who We Back") */
    statement?: { num: string; title: string; text: string };
    serve?: { num: string; title: string; chips: string[]; note?: string };
    /** Industries with descriptions (AppForge style) — partner-jaisi grid */
    serveCards?: {
      num: string;
      title: string;
      cards: { title: string; desc: string }[];
    };
    expertise?: { num: string; title: string; chips: string[] };
    framework?: {
      num: string;
      title: string;
      intro?: string;
      steps: { title: string; desc: string }[];
      note?: string;
    };
    philosophy?: { num: string; title: string; text: string };
    reasons?: {
      num: string;
      title: string;
      items: { lead: string; text: string }[];
    };
    ecosystem?: { num: string; title: string; text: string };
    globalLocal?: {
      num: string;
      title: string;
      local: string;
      global: string;
    };
    partner?: {
      num: string;
      title: string;
      cards: { title: string; desc: string }[];
      closing: string;
    };
  };
  /** Optional: "Who We Are" section ka custom content (title + paragraphs) */
  about?: {
    title: string;
    paragraphs: string[];
    /** Right column — Leadership Message */
    leadership?: {
      quote: string;
      name: string;
      role: string;
    };
    /** Image carousel ki jagah Corporate Snapshot table */
    snapshot?: { label: string; value: string }[];
    /** Snapshot ke baad numbered editorial sections (04, 05, 06...) */
    sections?: {
      num: string;
      title: string;
      paragraphs: string[];
      chipsIntro?: string;
      chips?: string[];
    }[];
  };
}

export const entities: Entity[] = [
  {
    slug: "digital-systems",
    type: "company",
    name: "Digital Systems",
    tagline: "The Enterprise Intelligence & Digital Transformation Company",
    headline: "The Enterprise Intelligence & Digital Transformation Company",
    summary:
      "Digital Systems designs, engineers, and continuously modernizes the enterprise technology foundations organizations run on — platforms, systems, data, and AI infrastructure. It replaces fragmented legacy environments with unified, secure, and intelligent enterprise ecosystems built to scale, serving as the enterprise architecture and intelligence backbone of the entire KOT ecosystem.",
    purpose: [
      "Design and engineer enterprise-grade digital platforms",
      "Modernize legacy systems and business applications",
      "Embed AI, data, and analytics into core business systems",
      "Enable secure, resilient cloud infrastructure and enterprise operations",
    ],
    services: [
      { title: "Enterprise Platform Engineering", desc: "Enterprise-grade digital platforms that integrate business operations, data, and services." },
      { title: "ERP & Business Systems Modernization", desc: "Implementing and modernizing ERP, finance, human capital, and core business systems." },
      { title: "Enterprise AI & Data Platforms", desc: "Data infrastructure, analytics platforms, and AI systems embedded into core operations." },
      { title: "Cloud & Infrastructure Modernization", desc: "Cloud-native architectures, containerization, and scalable computing environments." },
      { title: "Systems Integration & Interoperability", desc: "Connecting enterprise applications and third-party systems through secure integration frameworks." },
      { title: "Digital Transformation Consulting", desc: "Enterprise technology strategy, governance, and transformation roadmaps." },
    ],
    ecosystemRole:
      "Digital Systems is the shared technology foundation upon which the entire KOT Enterprises ecosystem is built — every subsidiary, from AppForge's applications to Platform Systems' marketplaces, runs on enterprise platforms and AI infrastructure it engineers.",
    capabilities: [
      { title: "Enterprise Platform Engineering", desc: "Enterprise-grade platforms that unify operations, data, and services." },
      { title: "ERP & Business Systems Modernization", desc: "Finance, human capital, and core systems, implemented or re-engineered." },
      { title: "Enterprise AI & Data Platforms", desc: "Data infrastructure, analytics, and AI embedded into daily operations." },
      { title: "Cloud & Infrastructure Modernization", desc: "Cloud-native architectures, containerization, scalable computing." },
      { title: "Systems Integration & Interoperability", desc: "Secure, standardized connections across your entire estate." },
      { title: "Enterprise Analytics & BI", desc: "Executive dashboards and decision-support built on trustworthy data." },
      { title: "Legacy Modernization", desc: "Outdated systems re-engineered into secure, future-ready platforms." },
      { title: "Digital Transformation Consulting", desc: "Strategy, governance, and roadmaps that survive contact with reality." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Industries We Serve",
        chips: [
          "Government & Public Sector",
          "Financial Services",
          "Healthcare",
          "Education",
          "Manufacturing",
          "Energy & Utilities",
          "Telecommunications",
          "Retail & Commerce",
          "Logistics & Transportation",
          "Non-Profit & Development Organizations",
        ],
      },
      expertise: {
        num: "09",
        title: "Technology Expertise",
        chips: [
          "Enterprise Architecture",
          "Cloud & Containerization",
          "Data Engineering & Analytics",
          "Enterprise AI & Automation",
          "Systems Integration & APIs",
          "Cybersecure Infrastructure",
          "ERP Ecosystems",
          "Business Intelligence",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Enterprise Intelligence Framework",
        steps: [
          { title: "Enterprise Assessment", desc: "Systems, data, infrastructure, security posture, digital maturity." },
          { title: "Enterprise Strategy", desc: "A technology roadmap aligned to your objectives and regulatory reality." },
          { title: "Architecture & Design", desc: "Data models, integration patterns, interoperability standards." },
          { title: "Platform Engineering", desc: "Build and modernize core platforms and cloud infrastructure." },
          { title: "Intelligent Integration", desc: "AI, automation, and analytics embedded into core systems." },
          { title: "Migration & Deployment", desc: "Secure migration and organization-wide rollout." },
          { title: "Continuous Optimization", desc: "Performance, security, and scalability, refined permanently." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "We embed AI where it makes enterprises measurably smarter: intelligent process automation, predictive infrastructure maintenance, enterprise search, decision support, anomaly and fraud detection, automated data quality, and natural-language interfaces to enterprise systems. Always with transparency, security, accountability, and human oversight of anything critical.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Digital Systems",
        items: [
          { lead: "We run on what we sell.", text: "The entire KOT ecosystem operates on our foundations." },
          { lead: "Architecture-led.", text: "We design estates, not projects." },
          { lead: "AI-native infrastructure.", text: "Intelligence engineered in, not retrofitted." },
          { lead: "Security-first.", text: "Every design decision passes a security test before a convenience test." },
          { lead: "Ecosystem-backed.", text: "Quality assurance from Flawless X, experience design from AppForge, deployment through Platform Systems — already integrated." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Digital Systems is the technology backbone of a ten-company ecosystem. AppForge builds user experiences on our platforms; Commerce X runs commerce on our ERP and AI systems; EpiTech deploys our institutional transformation solutions; Flawless X hardens everything we ship. When you engage us, you inherit that integration.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we serve Pakistan's digital transformation — public sector, financial services, education, and industry — with teams who understand the regulatory and operational terrain.",
        global: "Globally, we build to international architecture, security, and interoperability standards, making us a cost-advantaged, standards-compliant engineering partner for organizations anywhere.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Enterprises & institutions", desc: "Start with an Enterprise Assessment; leave with a roadmap you own." },
          { title: "Government & public sector", desc: "National digital transformation, secure infrastructure, and compliant modernization programs." },
          { title: "Investors", desc: "Enterprise-infrastructure exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Universities & researchers", desc: "Applied research in enterprise AI, data, and architecture; graduate pathways into engineering roles." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Digital Systems — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Digital Systems is the enterprise intelligence and digital transformation company of KOT Enterprises.",
      paragraphs: [
        "We design, engineer, and modernize the technology foundations organizations run on — platforms, systems, data, and AI infrastructure.",
        "We were created inside the KOT Enterprises group (founded in Lahore, 2018) to solve a problem every growing organization eventually hits: fragmented, aging technology that turns every new ambition into an integration project. We are also the technology backbone of the KOT ecosystem itself — every company in our group runs on foundations we engineer, which means we hold ourselves to the standard of a customer who never leaves.",
      ],
      leadership: {
        quote: "Enterprise technology should be the reason you can move fast — not the reason you can't. Digital Systems exists to make the foundation invisible: secure, integrated, intelligent, and ready for whatever you decide to build on it next.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Digital Systems — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Enterprise technology · Digital transformation · Enterprise AI" },
        { label: "Business Model", value: "Platform engineering, modernization, and transformation services" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Most organizations don't have a technology problem — they have a technology accumulation problem. Decades of systems bought separately, connected loosely, and maintained defensively. The result: data no one trusts, integrations no one owns, and an IT estate that consumes budget just standing still.",
            "Conventional IT providers manage that complexity. We remove it. Digital Systems replaces fragmented legacy environments with unified, intelligent enterprise ecosystems — secure, interoperable, AI-enabled, and engineered to evolve rather than expire.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are enterprise architects first and service providers second. Our purpose is to give organizations the technology foundation to operate securely, scale sustainably, and innovate continuously.",
            "Our vision is a future where enterprise technology is no longer a constraint on ambition, but the foundation that makes ambition possible — for governments, enterprises, and institutions operating at any scale.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are moving our clients beyond fragmented legacy environments toward unified, intelligent enterprise ecosystems. If a solution compromises one of the four non-negotiables below, it isn't finished.",
          ],
          chipsIntro: "Four non-negotiables shape every system we design:",
          chips: ["Security", "Interoperability", "Scalability", "Responsible AI"],
        },
      ],
    },
  },
  {
    slug: "appforge",
    type: "company",
    name: "AppForge",
    tagline: "The Human Experience Engineering Company",
    headline: "The Human Experience Engineering Company",
    summary:
      "AppForge designs and engineers intelligent, AI-native digital experiences that connect people with businesses, services, and artificial intelligence across mobile, wearable, immersive, and future computing platforms. Moving beyond traditional app development, it builds adaptive, accessible, human-centered products that make technology feel effortless.",
    purpose: [
      "Design AI-native mobile and cross-platform digital products",
      "Create voice-first, gesture-based, and wearable experiences",
      "Engineer accessible, inclusive digital services for all users",
      "Continuously improve engagement through behavioral intelligence and personalization",
    ],
    services: [
      { title: "Mobile Application Engineering", desc: "Secure, scalable, intelligent apps for Android, iOS, and cross-platform ecosystems." },
      { title: "AI-Native Applications", desc: "Conversational AI, predictive assistance, intelligent automation, and adaptive interfaces." },
      { title: "UX & UI Design", desc: "Research-driven experience and interface design focused on simplicity and usability." },
      { title: "Voice & Gesture Interfaces", desc: "Conversational AI assistants, multilingual voice systems, and touchless interaction design." },
      { title: "Wearable & IoT Experiences", desc: "Applications for smartwatches, health devices, smart homes, and connected environments." },
      { title: "Experience Modernization", desc: "Upgrading legacy digital products for usability, accessibility, security, and engagement." },
    ],
    ecosystemRole:
      "AppForge is the human-facing layer of the KOT ecosystem, building the user experiences that sit on top of Digital Systems' platforms and distributing them through Platform Systems — while partnering with Growth IQ, Visual Systems, and Flawless X for engagement, creativity, and quality.",
    capabilities: [
      { title: "Mobile Application Engineering", desc: "Secure, scalable, intelligent apps for Android, iOS, and cross-platform." },
      { title: "AI-Native Applications", desc: "Conversational AI, predictive assistance, and adaptive interfaces built in from day one." },
      { title: "UX & UI Design", desc: "Research-driven, focused on simplicity, accessibility, and usability." },
      { title: "Customer Experience Transformation", desc: "Digital journeys redesigned around behavioral insight." },
      { title: "Voice Experience Engineering", desc: "Conversational interfaces, AI assistants, multilingual voice systems." },
      { title: "Wearable & IoT Experiences", desc: "Smartwatches, health devices, industrial wearables, connected environments." },
      { title: "Gesture-Based Interfaces", desc: "Touchless interaction through computer vision and spatial computing." },
      { title: "Progressive Web Applications", desc: "Lightweight, offline-capable, native-like web apps." },
      { title: "Experience Modernization", desc: "Legacy products brought to modern standards of usability and trust." },
    ],
    details: {
      serveCards: {
        num: "08",
        title: "Industries We Serve",
        cards: [
          { title: "Healthcare", desc: "Patient engagement, remote monitoring, telemedicine, wellness — where trust and accessibility are non-negotiable." },
          { title: "Education", desc: "Learning apps, digital classrooms, and adaptive systems that keep students engaged." },
          { title: "Financial Technology", desc: "Banking, wallets, payments, investing — where every interaction must feel secure." },
          { title: "Supply Chain & Logistics", desc: "Fleet, warehouse, field workforce, and delivery apps built for real-world conditions." },
        ],
      },
      expertise: {
        num: "09",
        title: "Technology Expertise",
        chips: [
          "Conversational & Predictive AI",
          "Mobile (Android, iOS, Cross-Platform)",
          "Voice & Natural Language",
          "Wearables & IoT",
          "AR/VR/MR & Spatial Computing",
          "Smart TV, Automotive & Smart Home",
          "PWAs",
          "Behavioral Analytics",
          "Accessibility Engineering",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Human Experience Framework",
        steps: [
          { title: "Research", desc: "Understand the humans first: context, abilities, behaviors, frustrations." },
          { title: "Design", desc: "Shape the experience around simplicity, accessibility, and trust." },
          { title: "Engineering", desc: "Build it secure, scalable, cross-platform." },
          { title: "AI Integration", desc: "Add intelligence where it creates measurable user value." },
          { title: "Optimization", desc: "Measure real behavior, refine relentlessly." },
          { title: "Continuous Evolution", desc: "Adapt as users, technology, and platforms change." },
        ],
        note: "This is why our products don't age the way ordinary software does.",
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "We put AI into products wherever it creates measurable value for users — personalization, recommendations, voice assistance, prediction, smart notifications, automated support. Under four permanent commitments: transparency (users understand what the AI does), fairness (works for everyone), privacy (protected by design), and human control (AI enhances, never overrides).",
      },
      reasons: {
        num: "12",
        title: "Why Choose AppForge",
        items: [
          { lead: "Human-centered by discipline, not decoration.", text: "Experience engineering is the core competency." },
          { lead: "AI-native.", text: "Intelligence in the architecture, not on the interface." },
          { lead: "Future-platform ready.", text: "Designed for where computing is going." },
          { lead: "Accessible to everyone.", text: "Inclusive design is a standard, not an option." },
          { lead: "Trust as a feature.", text: "Privacy and security engineered into every product." },
          { lead: "Ecosystem-backed.", text: "The full KOT group stands behind every engagement." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Your product can be built on Digital Systems' enterprise backbone, validated by Flawless X's quality and accessibility engineering, brought to life by Visual Systems' creative work, distributed through Platform Systems, and scaled by Growth IQ — one group, one standard, no hand-off friction.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we build for Pakistan's realities: multilingual users, first-time digital adopters, bandwidth-constrained environments, and mobile-first behavior.",
        global: "Globally, that same discipline is our edge — products engineered for the hardest usability conditions perform anywhere, and we deliver them at globally competitive cost from a deep engineering talent pool.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Product owners & enterprises", desc: "Bring us a product, a journey, or just a frustration; we'll bring back an experience strategy." },
          { title: "Government & public sector", desc: "Citizen-facing digital services that ordinary people can actually use." },
          { title: "Investors", desc: "Exposure to AI-native product engineering inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Universities & researchers", desc: "HCI and applied-AI collaboration, student challenges, and graduate pathways." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "AppForge — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "AppForge is the human experience engineering company of KOT Enterprises.",
      paragraphs: [
        "We design and build intelligent digital products — mobile apps, voice interfaces, wearable experiences, and AI-native applications — that people genuinely enjoy using.",
        "We were created inside the KOT Enterprises group (founded in Lahore, 2018) to answer a simple observation: most software is built around technology, not around people. As computing moves beyond the smartphone into voice, gesture, wearables, and ambient AI, that gap will only widen. AppForge exists to close it.",
      ],
      leadership: {
        quote: "Anyone can ship an app. Very few can build an experience that earns a place in someone's daily life — simple, trustworthy, accessible, and quietly intelligent. That is the standard we hold ourselves to, and the standard we bring to every client.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "AppForge — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Digital product engineering · Human experience design" },
        { label: "Business Model", value: "Experience design and engineering services + AI-native product development" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Organizations everywhere face the same problem: their digital products work, but people don't love using them. Apps get downloaded and abandoned. Portals frustrate the customers they were built for. And every new wave of technology — AI assistants, wearables, voice, immersive computing — makes yesterday's product feel older, faster.",
            "Traditional development companies respond by building more software. That's the wrong answer, because the problem was never a shortage of software. The problem is experience. AppForge engineers intelligent experiences that evolve with users, with AI, and with whatever computing platform comes next.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We define ourselves not by the platforms we build for, but by the people we build for. Our purpose is to bridge the gap between what humans need and what technology can do — through AI-native applications, cross-platform products, voice and gesture interfaces, wearable and IoT experiences, and digital services accessible to everyone.",
            "Our vision is a future where technology disappears into the background, letting people accomplish what they need through intelligent, personalized, effortless interaction.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "The future of computing extends far beyond the smartphone. Our direction is to lead that shift — moving clients from traditional app development to human experience engineering: AI, human-centered design, behavioral science, accessibility, personalization, and continuous optimization.",
          ],
          chipsIntro: "One rule governs every product:",
          chips: ["Reduce Complexity", "Increase Trust"],
        },
      ],
    },
  },
  {
    slug: "venture-xcelerator",
    type: "company",
    name: "Venture Xcelerator",
    tagline: "The Venture Intelligence Company",
    headline: "The Venture Intelligence Company",
    summary:
      "Venture Xcelerator discovers, validates, builds, accelerates, finances, and scales innovative ventures. More than an incubator or VC firm, it is an end-to-end venture ecosystem that combines entrepreneurship, AI, investment, mentorship, and global market access to turn bold ideas into globally competitive enterprises.",
    purpose: [
      "Discover high-potential entrepreneurs and validate business ideas",
      "Build, incubate, and accelerate ventures from concept to commercialization",
      "Facilitate access to strategic investment and venture capital",
      "Expand startups into regional and international markets",
    ],
    services: [
      { title: "Startup Incubation", desc: "Structured guidance and essential resources for early-stage entrepreneurs." },
      { title: "Startup Acceleration", desc: "Strategic planning, operational support, and market expansion for growth-stage ventures." },
      { title: "Venture Building", desc: "Creating startups internally around market opportunities with multidisciplinary teams." },
      { title: "Venture Capital Facilitation", desc: "Investment readiness, pitch development, due diligence, and investor connections." },
      { title: "Founder Development", desc: "Leadership training, executive coaching, and entrepreneurial capability building." },
      { title: "Research Commercialization", desc: "Turning academic research and emerging technologies into viable businesses." },
    ],
    ecosystemRole:
      "Venture Xcelerator is the venture-creation engine of the KOT ecosystem, giving startups access to the full capabilities of every subsidiary — from Digital Systems' technology to Growth IQ's marketing — and it provides strategic leadership for the Challenge X platform.",
    capabilities: [
      { title: "Startup Incubation", desc: "Structured guidance and essential resources for early-stage founders." },
      { title: "Startup Acceleration", desc: "Strategic planning, operational support, and market expansion for growth-stage ventures." },
      { title: "Venture Building", desc: "Startups created internally around real market opportunities, with multidisciplinary teams." },
      { title: "Investment Facilitation", desc: "Investment readiness, pitch development, due diligence, and investor connection." },
      { title: "Founder Development", desc: "Leadership training, executive coaching, and lifelong mentorship." },
      { title: "Corporate Innovation", desc: "Helping established organizations launch innovation programs and work with startups." },
      { title: "Research Commercialization", desc: "Turning academic research into viable products and businesses." },
      { title: "Ecosystem Development", desc: "Networks connecting founders, investors, universities, governments, and corporations." },
    ],
    details: {
      statement: {
        num: "08",
        title: "Who We Back — Our Investment Philosophy",
        text: "We prioritize ventures with strong societal impact, real technological innovation, sustainable business models, ethical leadership, scalable operations, and global competitiveness — including responsible use of AI and environmental sustainability. Financial return matters; it is never the only measure.",
      },
      expertise: {
        num: "09",
        title: "Domain Expertise",
        chips: [
          "Venture Design & Business Modeling",
          "Market Validation Methodology",
          "Investment Readiness & Deal Structuring",
          "Founder Capability Development",
          "Research Commercialization & IP Strategy",
          "AI-Assisted Venture Analytics",
          "Cross-Border Expansion",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Venture Lifecycle",
        steps: [
          { title: "Discovery", desc: "Finding ideas, research, and talent through universities, industry, and competitions." },
          { title: "Validation", desc: "Market demand, feasibility, financial viability, strategic fit." },
          { title: "Venture Design", desc: "Business model, product strategy, IP strategy, governance." },
          { title: "Incubation", desc: "Infrastructure, mentorship, technical and legal support." },
          { title: "Acceleration", desc: "Customer acquisition, operational scaling, branding, investment readiness." },
          { title: "Investment", desc: "Seed, angel, VC, corporate, and grant financing pathways." },
          { title: "Global Expansion", desc: "International market entry, export readiness, compliance, partnerships." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI runs through our venture operations: opportunity discovery, market and trend analysis, business model evaluation, investment risk analysis, product-market-fit prediction, portfolio analytics, and financial forecasting. It augments expert judgment — it never replaces it — and it operates transparently and accountably.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Venture Xcelerator",
        items: [
          { lead: "End-to-end, not stage-bound.", text: "Idea to international, one ecosystem." },
          { lead: "Builders, not spectators.", text: "We create ventures ourselves — we know the work." },
          { lead: "Capital-connected.", text: "Structured pathways to angels, VCs, corporates, and grants." },
          { lead: "Research-friendly.", text: "A working bridge between universities and markets." },
          { lead: "Ecosystem-powered.", text: "Ten sister companies your venture can build with from day one." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "A startup here doesn't start from zero. Digital Systems provides enterprise technology and AI platforms; AppForge designs the product experience; Growth IQ drives customers and brand; Visual Systems creates the identity and story; Platform Systems provides distribution; Flawless X installs quality and governance; The Founders TX opens the room where investors and policymakers sit. That is a venture stack no standalone accelerator can offer.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we serve Pakistan's founders, universities, and Business Incubation Centers — turning a young, ambitious talent pool into companies and jobs.",
        global: "Globally, we build ventures for international markets from the start: export-ready, standards-compliant, and connected to international innovation ecosystems and investors.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Founders", desc: "Bring your idea or growth-stage venture; enter the Lifecycle at the stage you need." },
          { title: "Investors", desc: "Access a curated, investment-ready pipeline with structured due diligence support. info@groupofcompanies.com" },
          { title: "Universities & researchers", desc: "Commercialization pathways for research, joint incubation, student venture programs." },
          { title: "Government & development bodies", desc: "Entrepreneurship programs, national innovation challenges, and job-creation initiatives." },
          { title: "Corporations", desc: "Innovation programs, startup partnerships, and internal venture building." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Venture Xcelerator — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Venture Xcelerator is the venture intelligence company of KOT Enterprises.",
      paragraphs: [
        "We discover, validate, build, accelerate, finance, and scale ventures — supporting founders across the entire journey from first idea to international expansion.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), we exist because great ideas fail for predictable reasons: no validation discipline, no mentorship, no capital access, no ecosystem. We built the machine that removes those reasons.",
      ],
      leadership: {
        quote: "Entrepreneurship is the fastest engine of economic growth we know — but only when founders get more than encouragement. They need infrastructure, capital, honesty, and a network that opens doors. Venture Xcelerator was built to be all four.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Venture Xcelerator — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Venture building · Incubation & acceleration · Investment facilitation" },
        { label: "Business Model", value: "Venture development ecosystem — incubation, acceleration, venture building, investment readiness" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Most promising ideas never become companies — and most young companies never scale. Not because the ideas were bad, but because founders face the journey alone: unvalidated assumptions, no structured mentorship, investors they can't reach, and markets they can't enter.",
            "Conventional incubators fix one piece. Venture Xcelerator was built as an integrated venture ecosystem: entrepreneurship, research, investment, education, AI, and global market access in one platform. We don't just host startups — we build ventures.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are venture builders, not landlords. Our purpose is to accelerate innovation by building companies that create measurable economic and social value — discovering entrepreneurs, validating ideas with structured methodology, connecting founders to capital and mentors, commercializing research, and taking ventures international.",
            "Our vision: a future where every promising idea has access to the knowledge, capital, technology, mentorship, and partnerships it needs to become a successful enterprise.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are becoming the preferred venture-building ecosystem for founders, researchers, and innovators who want to create globally competitive enterprises — one framework supporting the complete lifecycle of a business, from discovery to global expansion.",
          ],
        },
      ],
    },
  },
  {
    slug: "epitech",
    type: "company",
    name: "EpiTech",
    tagline: "The Education Innovation & Industry Transformation Company",
    headline: "The Education Innovation & Industry Transformation Company",
    capabilities: [
      { title: "Institutional Transformation", desc: "Governance, curriculum, operations, and delivery modernized end-to-end." },
      { title: "Curriculum Development", desc: "Programs aligned with future industries, AI, sustainability, and innovation." },
      { title: "Faculty Development", desc: "Modern pedagogy, AI literacy, research capability, industry exposure." },
      { title: "Workforce Development", desc: "Technical, professional, leadership, and employability training." },
      { title: "Industry Linkages", desc: "Real collaboration between academia, government, and industry: research, internships, employment." },
      { title: "Entrepreneurship Development", desc: "Startup education, innovation challenges, incubation programs." },
      { title: "Research & Innovation", desc: "Interdisciplinary research and commercialization of knowledge." },
      { title: "Educational Technology", desc: "AI-powered learning platforms, digital classrooms, adaptive systems, institutional analytics." },
      { title: "Lifelong Learning", desc: "Executive education, certifications, reskilling and upskilling." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Who We Serve",
        chips: [
          "Universities & Colleges",
          "Schools",
          "Technical & Vocational Institutions",
          "Government Education Bodies",
          "Employers & Industry Associations",
          "Working Professionals",
          "Students and Out-of-School Youth (through group initiatives)",
        ],
      },
      expertise: {
        num: "09",
        title: "Domain & Technology Expertise",
        chips: [
          "Curriculum Design & Accreditation Alignment",
          "Learning Science & Pedagogy",
          "AI in Education (Adaptive Learning, Tutoring Systems, Learning Analytics)",
          "Institutional Analytics & Decision Support",
          "Assessment Technology",
          "Career & Employability Analytics",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Education Transformation Framework",
        steps: [
          { title: "Institutional Assessment", desc: "Readiness, governance, curriculum, faculty, technology, industry engagement." },
          { title: "Curriculum Modernization", desc: "Programs redesigned for future technologies and industries." },
          { title: "Faculty Development", desc: "Educators strengthened in pedagogy, AI, research, and industry exposure." },
          { title: "Student Capability", desc: "Technical, professional, entrepreneurial, and critical-thinking skills through experiential learning." },
          { title: "Industry Integration", desc: "Partnerships producing research, internships, and workforce readiness." },
          { title: "Employment & Entrepreneurship", desc: "Graduates prepared for jobs, startups, freelancing, and research careers." },
          { title: "Continuous Improvement", desc: "Outcomes measured, performance improved, permanently." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI improves learning outcomes and institutional efficiency: adaptive learning, intelligent tutoring, performance prediction, personalized pathways, AI-assisted curriculum design, administrative automation, and employability analytics. And one line we never cross: AI enhances education while preserving human mentorship, ethics, creativity, and critical thinking.",
      },
      reasons: {
        num: "12",
        title: "Why Choose EpiTech",
        items: [
          { lead: "Whole-journey ownership.", text: "Curriculum to career, one accountable partner." },
          { lead: "Regulator-aligned.", text: "A top-to-bottom national strategy built with authorities, not around them." },
          { lead: "The Three Es discipline.", text: "Every initiative must serve Education, Employment, or Entrepreneurship." },
          { lead: "Industry inside the classroom.", text: "Linkages that produce jobs, not just memoranda." },
          { lead: "Ecosystem-backed.", text: "Technology, incubation, media, and quality assurance from nine sister companies." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Digital Systems builds our institutional technology; AppForge creates learner-centered applications; Venture Xcelerator incubates student and faculty startups; Visual Systems produces educational media and immersive learning; Platform Systems hosts learning marketplaces; Flawless X supports quality and accreditation; The Founders TX connects institutions with policymakers and industry. One engagement, an entire education ecosystem.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we work with Pakistan's universities, HEC-aligned frameworks, and Business Incubation Centers to raise national employability and innovation capacity.",
        global: "Globally, we build to international education standards and partner with international institutions — preparing graduates for global careers and giving international partners a serious, governed counterpart in one of the world's largest youth populations.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Universities & institutions", desc: "Start with an Institutional Assessment; leave with a transformation roadmap." },
          { title: "Government & education authorities", desc: "National curriculum, employability, and education-technology programs." },
          { title: "Employers & industry", desc: "Graduate pipelines, curriculum input, and workforce upskilling built around your needs." },
          { title: "Investors", desc: "EdTech and workforce-development exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Researchers", desc: "Learning-science collaboration and commercialization pathways." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "EpiTech — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title:
        "EpiTech is the education innovation and industry transformation company of KOT Enterprises.",
      paragraphs: [
        "We help institutions transform how they educate — and help individuals become genuinely ready for the future of work, entrepreneurship, and lifelong learning.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), EpiTech exists because the gap between what institutions teach and what economies need keeps widening. We don't run internship programs at the edges of that gap. We own the whole journey across it.",
      ],
      leadership: {
        quote:
          "A nation's competitiveness is decided in its classrooms years before it shows up in its economy. EpiTech exists to make sure that when our graduates walk out, the future is something they build — not something that happens to them.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "EpiTech — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Education innovation · Workforce development · EdTech" },
        { label: "Business Model", value: "Institutional transformation, curriculum & faculty development, workforce programs, education technology" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Employers say graduates aren't ready. Graduates say jobs don't exist. Institutions are caught between curricula designed for a previous economy and a technological future arriving faster than accreditation cycles.",
            "Point solutions — a coding bootcamp here, an internship drive there — don't fix a structural problem. EpiTech transforms the structure: curriculum, faculty, student capability, industry linkage, and institutional operations, as one connected system. From modernized classrooms to employed, entrepreneurial graduates.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are education reformers with an industry engine. Our purpose is to bridge education and industry while preparing individuals for lifelong success — transforming institutions into innovation ecosystems, modernizing curricula, developing faculty, building future-ready skills, and making entrepreneurship a real career path.",
          ],
          chipsIntro: "Everything we do serves our Three Es:",
          chips: ["Education", "Employment", "Entrepreneurship"],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are becoming the leading organization connecting education with economic development — shifting institutions from academic achievement alone toward practical competence, creativity, leadership, ethics, and continuous learning. Our national approach is top-to-bottom: engagement with regulatory and educational authorities first (including the Higher Education Commission), then implementation across universities and institutions — respecting institutional autonomy throughout.",
          ],
        },
      ],
    },
    summary:
      "EpiTech transforms education from a traditional knowledge-delivery system into an intelligent, industry-connected ecosystem that prepares people for the future of work, entrepreneurship, and lifelong learning. It owns the complete education-to-industry journey — from curriculum modernization and faculty development to workforce readiness and institutional transformation.",
    purpose: [
      "Transform educational institutions into innovation ecosystems",
      "Modernize curricula and faculty capability for future industry needs",
      "Bridge academia and industry through experiential learning and partnerships",
      "Promote entrepreneurship, employability, and lifelong learning",
    ],
    services: [
      { title: "Institutional Transformation", desc: "Modernizing governance, curriculum, operations, and delivery for universities and schools." },
      { title: "Curriculum & Faculty Development", desc: "Future-ready academic programs and educator training in modern pedagogy and AI." },
      { title: "Workforce Development", desc: "Technical, professional, leadership, and employability training for students and professionals." },
      { title: "Industry Linkages", desc: "Academia-industry-government collaborations for research, internships, and graduate employment." },
      { title: "Entrepreneurship Development", desc: "Startup education, innovation challenges, and incubation programs." },
      { title: "Educational Technology & Lifelong Learning", desc: "AI-powered learning platforms plus executive education, certifications, and reskilling." },
    ],
    ecosystemRole:
      "EpiTech is the human capability engine of the KOT ecosystem, developing the talent, education systems, and workforce readiness that every other subsidiary depends on — and it jointly leads the Madad Farma community welfare initiative with Venture Xcelerator.",
  },
  {
    slug: "the-founders-tx",
    type: "company",
    name: "The Founders TX",
    tagline: "The Innovation Convergence Platform",
    headline: "The Innovation Convergence Platform",
    summary:
      "The Founders TX is KOT Enterprises' flagship innovation platform, uniting founders, investors, policymakers, educators, researchers, corporations, and governments to accelerate innovation, venture creation, and economic transformation. More than an annual event, it operates as a year-round ecosystem for collaboration, investment, knowledge exchange, and policy dialogue.",
    purpose: [
      "Connect innovators with investors, mentors, and strategic partners",
      "Bridge universities, industry, and government around shared challenges",
      "Facilitate innovation-driven policymaking and research commercialization",
      "Inspire and develop future generations of entrepreneurs",
    ],
    services: [
      { title: "Annual Innovation Summit", desc: "Flagship international event uniting government, industry, academia, investment, and technology leaders." },
      { title: "Founder Forums", desc: "Regular networking and collaboration events for entrepreneurs and startup founders." },
      { title: "Investor Connect", desc: "Structured investment readiness and pitching programs linking startups with investors." },
      { title: "Research Showcase", desc: "Events highlighting university research, patents, and commercialization opportunities." },
      { title: "Policy Roundtables", desc: "Collaborative discussions between policymakers, regulators, academics, and industry experts." },
      { title: "Innovation Awards & Leadership Dialogues", desc: "Recognition of outstanding innovation and executive forums with global thought leaders." },
    ],
    ecosystemRole:
      "The Founders TX is the primary convergence platform connecting every KOT Enterprises subsidiary — the stage where the Group's technologies, ventures, and ideas meet the world's founders, investors, and policymakers.",
    capabilitiesTitle: "Our Programs",
    capabilities: [
      { title: "Annual Innovation Summit", desc: "The flagship international gathering of government, industry, academia, investment, and technology leadership." },
      { title: "Industry Conferences", desc: "Sector-specific working conferences on emerging industries." },
      { title: "Founder Forums", desc: "Regular networking and collaboration for entrepreneurs." },
      { title: "Investor Connect", desc: "Structured investment-readiness and pitching programs linking startups with capital." },
      { title: "Research Showcase", desc: "University research, patents, and commercialization opportunities, in front of the people who can fund and deploy them." },
      { title: "Policy Roundtables", desc: "Policymakers, regulators, academics, and industry in structured dialogue." },
      { title: "Innovation Awards", desc: "Recognizing excellence in entrepreneurship, research, education, technology, sustainability, and social innovation." },
      { title: "Leadership Dialogues", desc: "Executive forums on the future of industries and economies." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Our Community",
        chips: [
          "Entrepreneurs",
          "Investors",
          "Students",
          "Researchers & Educators",
          "Corporate Leaders",
          "Government Officials",
          "Women Entrepreneurs",
          "Youth Innovators",
          "Social Enterprises",
          "Creative Industries",
          "International Delegates",
        ],
        note: "Equal access is a design principle: participation doesn't depend on organization size, geography, or title.",
      },
      expertise: {
        num: "09",
        title: "Platform Expertise",
        chips: [
          "Ecosystem Convening & Program Design",
          "Investment-Readiness Programming",
          "Policy Dialogue Facilitation",
          "Research-to-Industry Matchmaking",
          "International Delegation & Partnership Building",
          "Awards & Recognition Programs",
          "Hybrid and Digital Participation Infrastructure",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Convergence Model",
        intro: "Seven objectives drive every program we run:",
        steps: [
          { title: "Innovation Leadership", desc: "Emerging technologies and ideas promoted across all sectors." },
          { title: "Venture Creation", desc: "Startups and high-growth enterprises catalyzed." },
          { title: "Investment Promotion", desc: "Founders connected to angels, VCs, institutions, and corporates." },
          { title: "Policy Dialogue", desc: "Constructive engagement between builders and regulators." },
          { title: "Knowledge Exchange", desc: "Ideas, research, and best practices shared." },
          { title: "Global Collaboration", desc: "International partnerships that strengthen economies." },
          { title: "Talent Development", desc: "The next generation inspired and equipped." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI makes convergence work at scale: intelligent networking recommendations, founder-investor matchmaking, personalized event experiences, real-time multilingual communication, research analytics, and innovation trend forecasting. Always in service of the thing technology can't replace — a genuine human relationship formed in the right room.",
      },
      reasons: {
        num: "12",
        title: "Why Choose The Founders TX",
        items: [
          { lead: "Permanent, not annual.", text: "An ecosystem, not an event." },
          { lead: "Every stakeholder, one platform.", text: "Founders, capital, academia, and government together." },
          { lead: "Action-architected.", text: "Programs designed to end in partnerships, deals, and ventures." },
          { lead: "Neutral ground.", text: "A platform where collaboration beats competition." },
          { lead: "Ecosystem-delivered.", text: "Produced and powered by the full KOT group." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "The Founders TX is where the entire KOT ecosystem shows up at once: Digital Systems demonstrating enterprise transformation, AppForge showcasing future computing, Venture Xcelerator scouting and developing startups, EpiTech linking institutions, Commerce X convening the digital economy, Growth IQ driving engagement, Visual Systems producing the experience, Platform Systems powering digital participation, and Flawless X assuring quality throughout. For participants, that means every conversation started here has somewhere real to go.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we convene Pakistan's innovation ecosystem — its founders, universities, chambers, and ministries — around national priorities.",
        global: "Globally, we connect that ecosystem outward: international delegates, cross-border investment, and partnerships that put Pakistani innovation on the world's stage and bring the world's innovation here.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Founders & startups", desc: "Join Founder Forums and Investor Connect; get in the room that matters." },
          { title: "Investors", desc: "Curated access to an emerging market's most investable pipeline. info@groupofcompanies.com" },
          { title: "Government & policymakers", desc: "Co-design roundtables and national innovation programming." },
          { title: "Universities & researchers", desc: "Showcase research to industry and capital through the Research Showcase." },
          { title: "Corporations & sponsors", desc: "Sector conferences, awards, and leadership dialogues with real convening power." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "The Founders TX — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "The Founders TX is the flagship innovation convergence platform of KOT Enterprises.",
      paragraphs: [
        "We bring founders, investors, researchers, policymakers, educators, corporations, and governments into one working ecosystem — not once a year, but all year.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), The Founders TX exists because innovation's biggest bottleneck isn't ideas or capital — it's that the people who have each rarely meet in a room built for action.",
      ],
      leadership: {
        quote: "The future is not built by any single sector. It's built where governments, universities, industry, founders, and investors work on the same problem at the same table. The Founders TX is that table — permanently set.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "The Founders TX — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Innovation platforms · Events & convening · Ecosystem development" },
        { label: "Business Model", value: "Year-round innovation platform: summits, forums, investor programs, policy dialogue, awards" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Innovation ecosystems fail in fragments. Founders can't reach investors. Universities sit on research no industry sees. Policymakers regulate technologies they've never had explained to them by the people building them. Conferences promise connection, deliver business cards, and end.",
            "The Founders TX replaces the annual conference model with a permanent convergence platform — a year-round environment where ideas become partnerships, partnerships become ventures, and ventures create measurable economic impact. Collaboration over competition, action over applause.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are a neutral platform with a strong opinion: that every stakeholder — founder, student, minister, investor, professor, executive — has a role in building resilient economies, and deserves equal access to the room. Our purpose is to strengthen innovation ecosystems through collaboration, investment, knowledge exchange, leadership development, and strategic partnership.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are becoming the leading platform where innovation ecosystems converge — eliminating the barriers between industries, institutions, governments, founders, researchers, and investors, and directing that combined intelligence at national priorities and global challenges.",
          ],
        },
      ],
    },
  },
  {
    slug: "commerce-x",
    type: "company",
    name: "Commerce X",
    tagline: "The Commerce Intelligence Company",
    headline: "The Commerce Intelligence Company",
    summary:
      "Commerce X transforms how organizations create, exchange, and scale value through intelligent commerce. Going beyond online retail, it designs connected commerce ecosystems that integrate AI, digital platforms, financial services, supply chains, and customer intelligence — helping businesses of every size compete in the global digital economy.",
    purpose: [
      "Design AI-enabled commerce platforms and digital marketplaces",
      "Modernize traditional commerce operations and supply chains",
      "Integrate financial technologies and secure digital transactions",
      "Support SMEs and cross-border commerce growth",
    ],
    services: [
      { title: "Digital Commerce Transformation", desc: "Modernizing retail, wholesale, distribution, and services through intelligent ecosystems." },
      { title: "Marketplace Development", desc: "Multi-vendor marketplaces and B2B, B2C, and sector-specific commerce networks." },
      { title: "Financial Technology Integration", desc: "Digital payments, wallets, embedded finance, subscriptions, and financial automation." },
      { title: "Customer & Supply Chain Intelligence", desc: "Analytics, personalization, loyalty systems, and AI-optimized logistics and fulfillment." },
      { title: "Omnichannel Commerce", desc: "Unified customer experiences across stores, web, mobile, and social channels." },
      { title: "Cross-Border Commerce", desc: "Localization, compliance, payments, and logistics for international expansion." },
    ],
    ecosystemRole:
      "Commerce X is the commerce engine of the KOT ecosystem, turning the Group's platforms, applications, and intelligence into functioning digital economies — powered by Digital Systems' enterprise technology and Platform Systems' marketplace infrastructure.",
    capabilities: [
      { title: "Digital Commerce Transformation", desc: "Retail, wholesale, distribution, and services modernized end-to-end." },
      { title: "Marketplace Development", desc: "Multi-vendor, B2B, B2C, C2C, and sector-specific commerce networks, designed and operated." },
      { title: "FinTech Integration", desc: "Digital payments, wallets, embedded finance, invoicing, subscriptions." },
      { title: "Customer Intelligence", desc: "Analytics, recommendations, segmentation, loyalty, personalized engagement." },
      { title: "Supply Chain Intelligence", desc: "Procurement, inventory, warehousing, fulfillment, and distribution optimized with data and AI." },
      { title: "AI-Powered Sales Optimization", desc: "Pricing, demand forecasting, lead scoring, revenue growth." },
      { title: "Omnichannel Commerce", desc: "One seamless experience across stores, web, mobile, and social commerce." },
      { title: "Cross-Border Commerce", desc: "Localization, compliance, payments, and logistics for international expansion." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Industries We Serve",
        chips: [
          "Retail & Wholesale",
          "Manufacturing",
          "Healthcare Commerce",
          "Education Services",
          "Hospitality & Tourism",
          "Consumer Goods",
          "Financial Services",
          "Agriculture & Agribusiness",
          "Logistics & Distribution",
          "Digital Products",
          "SMEs",
          "Export-Oriented Businesses",
        ],
      },
      expertise: {
        num: "09",
        title: "Technology Expertise",
        chips: [
          "Commerce Platforms & Marketplaces",
          "Payment Systems & Embedded Finance",
          "AI for Pricing, Forecasting & Recommendations",
          "Customer Data & Analytics",
          "Supply Chain & Logistics Technology",
          "Omnichannel Infrastructure",
          "Cross-Border Commerce Systems",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Intelligent Commerce Lifecycle",
        steps: [
          { title: "Commerce Assessment", desc: "Business model, channels, supply chain, payments, digital readiness." },
          { title: "Commerce Strategy", desc: "A future-ready plan aligned to customers and objectives." },
          { title: "Digital Commerce Design", desc: "Online, offline, mobile, and intelligent channels unified." },
          { title: "Intelligent Operations", desc: "Automation, predictive inventory, intelligent pricing, digital payments." },
          { title: "Customer Intelligence", desc: "Behavioral analytics, personalization, loyalty." },
          { title: "Global Expansion", desc: "Scalable infrastructure for new regional and international markets." },
          { title: "Continuous Optimization", desc: "Analytics and AI improving performance permanently." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "We apply AI where commerce feels it: demand forecasting, dynamic pricing, product recommendations, fraud detection, automated support, inventory optimization, predictive logistics, and decision support — implemented responsibly, with governance, transparency, and privacy protection built in.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Commerce X",
        items: [
          { lead: "Ecosystem commerce, not e-commerce.", text: "We build the whole system, not the storefront." },
          { lead: "SME-serious.", text: "Digital transformation designed for the businesses that need it most." },
          { lead: "Intelligence in every transaction.", text: "Data working for you from day one." },
          { lead: "Cross-border ready.", text: "Built for export and international expansion." },
          { lead: "Ecosystem-backed.", text: "Platforms, experience design, growth, and quality from nine sister companies." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Digital Systems provides our enterprise and ERP backbone; AppForge builds the customer-facing experiences; Platform Systems supplies marketplace infrastructure and APIs; Growth IQ drives acquisition and market intelligence; Visual Systems creates product visualization and campaigns; Flawless X assures transaction integrity, security, and compliance. One engagement, a complete commerce stack.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we digitize Pakistan's retailers, manufacturers, agribusinesses, and SMEs — and connect them to national payment and logistics rails.",
        global: "Globally, we build export-ready commerce: localization, international payments, and compliance that let local businesses sell to the world, and give international businesses a capable partner into South Asian markets.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Businesses & SMEs", desc: "Start with a Commerce Assessment; leave with a transformation roadmap sized to your reality." },
          { title: "Enterprises", desc: "Marketplace builds, omnichannel unification, and supply-chain intelligence at scale." },
          { title: "Investors", desc: "Commerce-infrastructure exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Government & trade bodies", desc: "Digital economy programs, export enablement, and SME digitization at national scale." },
          { title: "Universities & researchers", desc: "Commerce analytics and digital-economy research collaboration." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Commerce X — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Commerce X is the commerce intelligence company of KOT Enterprises.",
      paragraphs: [
        "We help organizations transform how they create, exchange, and scale value — through intelligent commerce platforms, digital marketplaces, financial technology, supply chain intelligence, and AI-driven customer engagement.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), Commerce X exists because commerce has outgrown the online store. Modern commerce is an ecosystem — customers, suppliers, logistics, payments, and data — and most businesses are still running it in pieces.",
      ],
      leadership: {
        quote: "The businesses that win the next decade won't just sell online — they'll operate intelligent commerce ecosystems where every transaction teaches them something. Commerce X builds those ecosystems, for enterprises and for the SMEs that are the backbone of our economy.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Commerce X — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Digital commerce · Marketplaces · FinTech integration · Supply chain intelligence" },
        { label: "Business Model", value: "Commerce transformation services + marketplace design, development, and operation" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Most businesses digitized their storefront and stopped. Behind it: manual inventory, disconnected payments, guesswork pricing, supply chains that surprise them, and customer data they collect but never use. Meanwhile competitors — often global ones — operate commerce as an intelligent, automated, connected system.",
            "Commerce X closes that gap. We design commerce ecosystems where sales channels, payments, logistics, and customer intelligence work as one — so every interaction produces insight, efficiency, and a longer customer relationship.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are commerce architects. Our purpose is to enable businesses to create, exchange, and scale value through intelligent commerce systems — modernizing traditional operations, building marketplaces, integrating financial technology, optimizing supply chains, enabling cross-border trade, and bringing SMEs into the digital economy.",
            "Our vision: commerce that is intelligent, interconnected, secure, and accessible to organizations of every size.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are moving commerce beyond transactions toward intelligent value networks — a unified operating model integrating business strategy, technology, AI, logistics, finance, and customer experience, where every interaction generates insight and trust.",
          ],
        },
      ],
    },
  },
  {
    slug: "growth-iq",
    type: "company",
    name: "Growth IQ",
    tagline: "The Growth Intelligence Company",
    headline: "Growth powered by intelligence, not intuition",
    summary:
      "Growth IQ helps organizations achieve sustainable growth by integrating strategy, branding, customer intelligence, marketing, AI, and revenue optimization into one growth framework. Rather than delivering isolated marketing campaigns, it acts as a strategic growth partner that replaces intuition with intelligence and short-term promotion with long-term value creation.",
    purpose: [
      "Develop intelligent, data-driven growth strategies",
      "Build globally competitive brands and stronger customer relationships",
      "Improve customer acquisition, retention, and revenue performance",
      "Embed AI and analytics into marketing and business decision-making",
    ],
    services: [
      { title: "Strategic Business Consulting", desc: "Long-term growth strategy, market positioning, and competitive differentiation." },
      { title: "Brand Strategy", desc: "Corporate identities, positioning frameworks, messaging, and reputation management." },
      { title: "Marketing Intelligence", desc: "Market research, customer analytics, competitor analysis, and demand forecasting." },
      { title: "Digital Marketing", desc: "Integrated campaigns across search, social, email, content, and emerging platforms." },
      { title: "Customer Experience Management", desc: "Journey optimization through research, personalization, and loyalty strategies." },
      { title: "AI-Powered Marketing & Analytics", desc: "Marketing automation, predictive analytics, executive dashboards, and revenue optimization." },
    ],
    ecosystemRole:
      "Growth IQ is the growth and brand engine of the KOT ecosystem, leading branding, customer acquisition, and market intelligence for every subsidiary — from startup growth at Venture Xcelerator to audience engagement for The Founders TX.",
    capabilities: [
      { title: "Strategic Business Consulting", desc: "Long-term growth strategy, positioning, and competitive differentiation." },
      { title: "Brand Strategy", desc: "Corporate identity, positioning frameworks, messaging architecture, reputation management." },
      { title: "Marketing Intelligence", desc: "Market research, customer analytics, competitor analysis, demand forecasting." },
      { title: "Digital Marketing", desc: "Integrated campaigns across search, social, email, and content — run to targets, not impressions." },
      { title: "Customer Experience Management", desc: "Journeys, personalization, service design, and loyalty." },
      { title: "Revenue Growth Strategy", desc: "Sales optimization, pricing, acquisition models, and forecasting." },
      { title: "Business Analytics", desc: "Executive dashboards and performance systems leadership actually uses." },
      { title: "AI-Powered Marketing", desc: "Automation, predictive analytics, recommendations, conversational AI." },
      { title: "Corporate Communications", desc: "Stakeholder engagement, reputation, and public trust." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Industries We Serve",
        chips: [
          "Technology",
          "Financial Services",
          "Healthcare",
          "Education",
          "Manufacturing",
          "Retail & Commerce",
          "Hospitality & Tourism",
          "Logistics",
          "Real Estate",
          "Government & Public Sector",
          "Non-Profits",
          "Startups & High-Growth Enterprises",
        ],
      },
      expertise: {
        num: "09",
        title: "Technology Expertise",
        chips: [
          "Marketing Automation & CRM Ecosystems",
          "Predictive Analytics & Customer Modeling",
          "AI Segmentation & Personalization",
          "Campaign & Funnel Analytics",
          "Sentiment & Competitive Intelligence",
          "Revenue and CLV Analytics",
          "Executive Decision-Support Systems",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Growth Intelligence Framework",
        steps: [
          { title: "Business Assessment", desc: "Capabilities, brand position, customer perception, market opportunity." },
          { title: "Growth Strategy", desc: "A comprehensive plan aligned to vision and market dynamics." },
          { title: "Brand Development", desc: "Identity, positioning, value propositions, messaging." },
          { title: "Customer Intelligence", desc: "Behavior, trends, and engagement patterns turned into decisions." },
          { title: "Market Expansion", desc: "New segments, geographies, and partnerships." },
          { title: "Revenue Optimization", desc: "Pricing, sales effectiveness, and customer lifetime value." },
          { title: "Continuous Growth", desc: "Measured indicators, data-driven improvement, permanently." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI sharpens every stage of growth: segmentation, predictive customer behavior, recommendation systems, lead scoring, campaign optimization, sentiment analysis, sales forecasting, and CLV analytics. It complements human creativity and strategic judgment — with transparency, fairness, and privacy as standing conditions.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Growth IQ",
        items: [
          { lead: "Strategy before spend.", text: "No campaign without a growth thesis." },
          { lead: "Measured or it didn't happen.", text: "Every initiative accountable to business outcomes." },
          { lead: "Brand-builders, not just lead-buyers.", text: "Long-term equity and short-term revenue, together." },
          { lead: "AI with judgment.", text: "Intelligence that informs human strategy, not replaces it." },
          { lead: "Ecosystem-backed.", text: "Product, platform, creative, and quality partners one door away." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Digital Systems provides analytics infrastructure; AppForge builds the customer-facing experiences we optimize; Visual Systems creates the campaigns and brand storytelling; Commerce X connects growth to transactions; Platform Systems provides the digital rails; Flawless X measures quality and brand trust; The Founders TX gives our clients visibility where investors and partners gather. Growth here is never a silo.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we bring international-grade growth strategy to Pakistani enterprises, exporters, and startups — grounded in local consumer behavior and market structure.",
        global: "Globally, we serve international clients with data-driven strategy and execution at a compelling cost base, and help them read and enter South Asian markets with local intelligence.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Businesses & enterprises", desc: "Start with a Business Assessment; leave knowing exactly where growth will come from." },
          { title: "Startups", desc: "Investor-grade positioning, go-to-market, and traction systems." },
          { title: "Investors", desc: "Growth-services exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Government & institutions", desc: "Public communication, program engagement, and initiative marketing." },
          { title: "Universities & researchers", desc: "Consumer-behavior and marketing-analytics collaboration." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Growth IQ — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Growth IQ is the growth intelligence company of KOT Enterprises.",
      paragraphs: [
        "We help organizations grow sustainably — by integrating strategy, branding, customer intelligence, AI, digital marketing, and revenue optimization into one connected system.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), Growth IQ exists because most \"growth\" spending is really promotion spending — campaigns without strategy, brands without positioning, data without decisions. We built the company that connects them.",
      ],
      leadership: {
        quote: "Sustainable growth is never an accident and never just a campaign. It's the result of a system — strategy, brand, customer understanding, and execution working together. Growth IQ builds that system for organizations that are done guessing.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Growth IQ — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Growth strategy · Brand · Marketing intelligence · Revenue optimization" },
        { label: "Business Model", value: "Strategic growth partnership — consulting, brand, marketing, analytics, AI-powered engagement" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Organizations spend more than ever on marketing and grow slower than expected. The reason is structural: agencies run campaigns, consultants write strategies, analysts produce dashboards — and nobody connects them. Promotion gets funded; growth doesn't happen.",
            "Growth IQ replaces intuition with intelligence and promotion with strategy. We build integrated growth ecosystems where brand, customer insight, market intelligence, and revenue optimization work as one measurable system — so every initiative has to earn its place with business outcomes.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are a strategic growth partner, not a marketing agency. Our purpose is to transform how organizations grow: developing intelligent growth strategies, building globally competitive brands, strengthening customer relationships, improving acquisition and retention, and raising revenue performance — ethically and sustainably.",
            "Our vision: organizations that no longer rely on promotional bursts, but on intelligent systems that continuously create value for customers, employees, shareholders, and society.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are leading the transition from conventional marketing to integrated growth ecosystems powered by data, technology, creativity, and AI — where every growth initiative is accountable to measurable business outcomes, customer trust, and long-term profitability.",
          ],
        },
      ],
    },
  },
  {
    slug: "visual-systems",
    type: "company",
    name: "Visual Systems",
    tagline: "The Visual Intelligence & Creative Production Company",
    headline: "Turning imagination into intelligent visual experiences",
    summary:
      "Visual Systems transforms imagination into intelligent visual experiences — spanning digital storytelling, cinematic and virtual production, animation, gaming, immersive media, and AI-powered creative technologies. Beyond a traditional creative agency, it designs, produces, distributes, and monetizes visual ecosystems that inspire, educate, and entertain.",
    purpose: [
      "Produce high-quality digital content and cinematic productions",
      "Develop immersive AR, VR, and interactive experiences",
      "Build games and interactive entertainment with original IP",
      "Empower creators and preserve cultural identity through digital storytelling",
    ],
    services: [
      { title: "Branding & Design", desc: "Corporate identities, brand guidelines, and complete visual communication systems." },
      { title: "Digital Content Production", desc: "Videos, documentaries, educational content, campaigns, and multimedia assets." },
      { title: "Animation & Motion Graphics", desc: "2D and 3D animation, explainers, and visual simulations." },
      { title: "Film & Virtual Production", desc: "Cinematic content using virtual studios, LED stages, and AI-assisted workflows." },
      { title: "Gaming & Interactive Entertainment", desc: "Games, gamified learning, simulations, and a dedicated gaming division building original IP." },
      { title: "Immersive Media", desc: "VR, AR, mixed reality, and spatial computing experiences across industries." },
    ],
    ecosystemRole:
      "Visual Systems is the creative engine of the KOT ecosystem, delivering storytelling, media production, and immersive experiences for every subsidiary — and it leads the creative concept and guest experience design of the Platform 9¾ initiative.",
    capabilities: [
      { title: "Creative Strategy", desc: "Visual communication strategy aligned to goals and audiences." },
      { title: "Branding & Design", desc: "Identities, guidelines, publications, packaging, campaign design." },
      { title: "Digital Content Production", desc: "Video, documentary, educational, promotional, and corporate content." },
      { title: "Animation & Motion Graphics", desc: "2D/3D animation, explainers, simulations." },
      { title: "Film & Virtual Production", desc: "Cinematic content with virtual studios, LED stages, and AI-assisted workflows." },
      { title: "Gaming & Interactive Entertainment", desc: "Games, gamified learning, simulation environments." },
      { title: "Immersive Media", desc: "VR, AR, MR, and spatial experiences for education, healthcare, industry, tourism, entertainment." },
      { title: "Digital Publishing", desc: "Interactive publications, e-learning content, knowledge portals." },
      { title: "Creator Economy Services", desc: "Production, IP development, distribution, audience growth, monetization." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Industries We Serve",
        chips: [
          "Media & Entertainment",
          "Education",
          "Healthcare",
          "Government Communications",
          "Tourism & Culture",
          "Marketing & Advertising",
          "Technology",
          "Financial Services",
          "Commerce & Retail",
          "Manufacturing",
          "Gaming",
          "Creative Economy",
        ],
      },
      expertise: {
        num: "09",
        title: "Technology Expertise",
        chips: [
          "Virtual Production & LED Stage Workflows",
          "2D/3D Animation & VFX",
          "Game Engines & Interactive Development",
          "VR/AR/MR & Spatial Computing",
          "AI-Assisted Content Creation & Editing",
          "Automated Localization (Subtitling, Translation, Voice)",
          "Audience Analytics & Content Performance",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Creative Intelligence Framework",
        steps: [
          { title: "Creative Discovery", desc: "Objectives, audiences, business goals, cultural context." },
          { title: "Concept Development", desc: "Story, script, visual identity, artistic direction." },
          { title: "Design & Production", desc: "Graphics, animation, film, interactive, and immersive builds." },
          { title: "Intelligent Enhancement", desc: "AI for editing, effects, personalization, accessibility, and multilingual adaptation." },
          { title: "Distribution", desc: "Digital platforms, broadcast, streaming, education, gaming, and enterprise channels." },
          { title: "Monetization", desc: "Licensing, subscriptions, marketplaces, and IP management." },
          { title: "Continuous Evolution", desc: "Engagement measured, impact proven, work improved." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI accelerates our craft: assisted content generation, intelligent editing, automated subtitling and translation, visual effects, personalization, audience analytics, and workflow automation. One principle is permanent: AI supports and amplifies human creativity — it never replaces artistic vision.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Visual Systems",
        items: [
          { lead: "Craft plus intelligence.", text: "Storytelling discipline with an AI-assisted production engine." },
          { lead: "Full-stack creative.", text: "Strategy to distribution to monetization, one partner." },
          { lead: "Immersive-ready.", text: "VR, AR, gaming, and virtual production in-house." },
          { lead: "Culturally authentic, globally fluent.", text: "Stories rooted here, built to travel." },
          { lead: "Ecosystem-backed.", text: "Technology, quality, growth, and distribution from nine sister companies." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Digital Systems provides our AI and technology infrastructure; AppForge embeds our creative work into digital products; Growth IQ turns creative into market performance; Commerce X applies our visualization to selling; Platform Systems distributes creative assets and IP through digital marketplaces; EpiTech partners on educational media; Flawless X assures production quality and accessibility; The Founders TX puts our storytelling on the ecosystem's biggest stages.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we serve Pakistan's brands, institutions, and cultural heritage — producing work that preserves identity and raises the standard of national storytelling.",
        global: "Globally, we offer international clients premium creative production at a compelling cost base, and carry Pakistani stories, games, and IP to worldwide audiences through streaming, gaming, and digital distribution.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Brands & enterprises", desc: "Creative strategy through production and performance, one accountable team." },
          { title: "Broadcasters, studios & platforms", desc: "Co-production, animation, and virtual production capacity." },
          { title: "Investors", desc: "Creative-economy and gaming exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Government & cultural bodies", desc: "Public communication, tourism, and cultural preservation projects." },
          { title: "Universities & creators", desc: "Training, creator-economy services, and collaborative productions." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Visual Systems — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Visual Systems is the visual intelligence and creative production company of KOT Enterprises.",
      paragraphs: [
        "We turn ideas into visual experiences — film, animation, gaming, immersive media, brand design, and AI-powered creative production — that inform, educate, entertain, and move people.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), Visual Systems exists because visual communication has become the most influential medium in business, education, entertainment, and public life — and it deserves an organization that treats creativity as seriously as engineering.",
      ],
      leadership: {
        quote: "Every organization is now a media company, whether it admits it or not. The ones people remember are the ones who tell their story with craft. Visual Systems exists to bring that craft — and the intelligence behind it — to everyone with a story worth telling.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Visual Systems — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Creative production · Media & entertainment · Gaming · Immersive technology" },
        { label: "Business Model", value: "Creative services + content production, distribution, and IP monetization" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Attention is the scarcest resource in every market — and most organizations are losing the fight for it. Content gets produced but not remembered. Brands communicate but don't connect. Education informs but doesn't engage. Meanwhile audiences have moved to interactive, immersive, personalized media that most producers can't deliver.",
            "Visual Systems closes the distance between what organizations need to say and what audiences actually experience. We combine storytelling craft, production technology, AI, and immersive media into creative work built to be felt — and built to perform.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are a creative intelligence organization — beyond a design studio, beyond a production house. Our purpose is to create intelligent visual ecosystems: high-quality digital content, animation, cinematic production, games and interactive entertainment, VR/AR experiences, and digital publishing — plus the creator-economy services that help artists monetize their work.",
            "Our vision: visual experiences that are intelligent, interactive, personalized, and globally accessible — including stories that preserve cultural identity while earning global audiences.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are building complete creative ecosystems, not one-off content. That means integrating creativity, AI, and emerging technology into every stage of production — and maintaining three commitments no project overrides: artistic excellence, ethical responsibility, and cultural authenticity.",
            "We are also building a dedicated Gaming Division — original IP, interactive entertainment, simulation, esports, and educational games — toward the long-term vision of a globally recognized gaming house from Pakistan.",
          ],
        },
      ],
    },
  },
  {
    slug: "platform-systems",
    type: "company",
    name: "Platform Systems",
    tagline: "The Platform Economy Company",
    headline: "Building the platforms that power digital economies",
    summary:
      "Platform Systems designs, builds, operates, and scales intelligent digital platforms that connect people, businesses, governments, developers, and AI into integrated digital economies. It creates the marketplaces, API ecosystems, and platform infrastructure that let organizations everywhere participate in the future platform economy.",
    purpose: [
      "Build enterprise digital platforms and AI-powered marketplaces",
      "Create API ecosystems and developer communities",
      "Enable secure digital collaboration and interoperability",
      "Contribute to national and international digital economy development",
    ],
    services: [
      { title: "Enterprise Platform Development", desc: "Enterprise-grade platforms integrating business operations, services, and stakeholders." },
      { title: "AI Marketplace Platforms", desc: "Marketplaces for AI models, intelligent agents, datasets, and machine learning services." },
      { title: "API Economy Solutions", desc: "API management platforms, developer portals, integration hubs, and monetization ecosystems." },
      { title: "Platform-as-a-Service", desc: "Cloud-native development, deployment, orchestration, and infrastructure management." },
      { title: "Data Exchange Platforms", desc: "Secure environments for data sharing, governance, and trusted inter-organization collaboration." },
      { title: "Smart City & Industry Platforms", desc: "Integrated platforms for urban services and sectors like healthcare, education, and finance." },
    ],
    ecosystemRole:
      "Platform Systems is the digital backbone of the KOT ecosystem — the infrastructure through which every capability developed across the Group is securely deployed, discovered, consumed, and continuously improved.",
    capabilities: [
      { title: "Enterprise Platform Development", desc: "Platforms that integrate operations, services, and stakeholders." },
      { title: "AI Marketplace Platforms", desc: "Marketplaces for AI models, agents, datasets, and intelligent services." },
      { title: "API Economy Solutions", desc: "API management, developer portals, integration hubs, monetization." },
      { title: "Digital Capability Exchange", desc: "Platforms to publish, discover, and integrate digital capabilities." },
      { title: "Platform-as-a-Service", desc: "Cloud-native development, deployment, and orchestration environments." },
      { title: "Developer Ecosystems", desc: "SDKs, documentation, sandboxes, certification, and innovation challenges." },
      { title: "Data Exchange Platforms", desc: "Secure, governed data sharing between organizations." },
      { title: "Smart City Platforms", desc: "Urban governance, transport, utilities, health, education, and citizen services." },
      { title: "Industry Platforms", desc: "Sector-specific ecosystems across healthcare, education, finance, logistics, agriculture, energy, and government." },
    ],
    details: {
      statement: {
        num: "08",
        title: "National Digital Infrastructure",
        text: "We design and operate platforms that serve national and regional transformation — digital identity ecosystems, government service platforms, digital health and education platforms, commerce ecosystems, workforce marketplaces, research collaboration platforms, open data ecosystems, and smart city infrastructure — always in accordance with applicable law, regulatory requirements, and international standards.",
      },
      expertise: {
        num: "09",
        title: "Technology Expertise",
        chips: [
          "Cloud-Native & Microservices Architecture",
          "API Management & Integration",
          "AI Orchestration & Recommendation Systems",
          "Identity & Security Infrastructure",
          "Data Governance & Interoperability",
          "Marketplace & Payment Infrastructure",
          "Developer Tooling & SDKs",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Platform Economy Framework",
        steps: [
          { title: "Ecosystem Assessment", desc: "Stakeholders, business models, communities, infrastructure, opportunity." },
          { title: "Platform Strategy", desc: "Business model, governance, monetization, participant roles, value exchange." },
          { title: "Platform Engineering", desc: "Scalable, cloud-native, modular, secure builds." },
          { title: "Ecosystem Integration", desc: "Enterprises, developers, services, payments, and external platforms connected." },
          { title: "Marketplace Enablement", desc: "Live exchange of services, products, AI capabilities, APIs, and data." },
          { title: "Platform Intelligence", desc: "Analytics, AI, and automation optimizing performance continuously." },
          { title: "Continuous Evolution", desc: "Governance, cybersecurity, and innovation sustaining the ecosystem." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI is foundational to our platforms: intelligent governance, automated orchestration, recommendations, API discovery, digital assistants, fraud detection, identity intelligence, security automation, and autonomous infrastructure optimization — with transparency, accountability, and ethical governance as operating conditions, not add-ons.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Platform Systems",
        items: [
          { lead: "Operators, not just builders.", text: "We run platforms, so we design for the long term." },
          { lead: "Governance-first.", text: "Trust engineered into the platform, not promised around it." },
          { lead: "Network effects by design.", text: "Ecosystems structured so every participant gains from growth." },
          { lead: "Public-interest capable.", text: "Built for national infrastructure standards and regulatory scrutiny." },
          { lead: "Ecosystem-backed.", text: "The full KOT group deploys through — and strengthens — every platform we run." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Platform Systems is the digital backbone of the KOT ecosystem: Digital Systems deploys enterprise solutions through us; AppForge connects user applications to our services; Commerce X runs marketplaces on our rails; Visual Systems distributes creative IP through our platforms; EpiTech delivers learning and certification platforms; Venture Xcelerator launches venture ecosystems; Flawless X assures reliability, security, and continuity. When you build on our platforms, that entire ecosystem is available as capability.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we build the digital infrastructure Pakistan's economy needs — from SME marketplaces to public-service platforms — with full regulatory alignment.",
        global: "Globally, we engineer to international interoperability and security standards, serving international clients and connecting local ecosystems to global digital economies.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Enterprises", desc: "Turn your product into a platform, or your industry position into an ecosystem." },
          { title: "Governments & public bodies", desc: "National platforms, smart city infrastructure, and citizen services designed compliance-first." },
          { title: "Developers", desc: "SDKs, sandboxes, certification, and a growing ecosystem to build within." },
          { title: "Investors", desc: "Platform-economy infrastructure exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Universities & researchers", desc: "Research collaboration platforms and digital-economy studies." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Platform Systems — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Platform Systems is the platform economy company of KOT Enterprises.",
      paragraphs: [
        "We design, build, operate, and govern intelligent digital platforms — marketplaces, API ecosystems, data exchanges, and national digital infrastructure — that connect people, businesses, governments, and technologies into working digital economies.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), Platform Systems exists because the future economy runs on platforms, not products — and someone has to build the platforms.",
      ],
      leadership: {
        quote: "Every transformative digital economy of the last twenty years was built on a platform someone had the nerve to construct. Platform Systems exists so that businesses, governments, and communities here don't just participate in the platform economy — they own pieces of it.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Platform Systems — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Digital platforms · Marketplaces · API economy · Digital infrastructure" },
        { label: "Business Model", value: "Platform design, engineering, operation, and governance; PaaS and ecosystem services" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Organizations keep building isolated applications in a world that rewards connected ecosystems. The result: software that can't talk to partners, data that can't be shared safely, services that can't scale beyond their silo — and whole industries watching platform players capture the value they create.",
            "Platform Systems builds the alternative: interoperable digital ecosystems where services, data, AI, and commerce converge — with network effects working for you, governance you can trust, and infrastructure engineered to scale.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are platform architects and operators. Our purpose is to enable intelligent digital economies: enterprise platforms, AI-powered marketplaces, API ecosystems, developer communities, data exchanges, smart city platforms, and sector platforms for healthcare, education, finance, logistics, agriculture, and government.",
            "Our vision: every organization participating in connected digital ecosystems that remove barriers to innovation and accelerate economic development.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are leading the transition from isolated systems to interconnected digital ecosystems. Five commitments govern every platform we build: openness, interoperability, scalability, resilience, and responsible governance.",
          ],
        },
      ],
    },
  },
  {
    slug: "flawless-x",
    type: "company",
    name: "Flawless X",
    tagline: "The Quality Intelligence & Operational Excellence Company",
    headline: "Excellence, security, and trust in everything",
    summary:
      "Flawless X ensures that every product, service, process, and platform meets the highest standards of quality, security, compliance, and resilience. Going far beyond software testing, it delivers enterprise-wide quality intelligence — uniting quality engineering, cybersecurity assurance, governance, risk, and continuous improvement into one excellence framework.",
    purpose: [
      "Develop intelligent quality management systems and engineering practices",
      "Strengthen cybersecurity maturity, governance, and regulatory compliance",
      "Improve operational efficiency, resilience, and certification readiness",
      "Build cultures of continuous improvement and excellence",
    ],
    services: [
      { title: "Quality Engineering", desc: "Embedding quality across software, platforms, products, and enterprise operations." },
      { title: "Software Testing", desc: "Functional, automation, performance, usability, accessibility, and security testing." },
      { title: "Cybersecurity Assurance", desc: "Vulnerability assessments, penetration testing, security governance, and resilience testing." },
      { title: "Governance, Risk & Compliance", desc: "Governance frameworks, enterprise risk management, and regulatory compliance programs." },
      { title: "Certification Readiness & Auditing", desc: "Gap assessments, audit preparation, and quality, technology, and cybersecurity audits." },
      { title: "Operational Resilience", desc: "Business continuity, disaster recovery, crisis management, and preparedness planning." },
    ],
    ecosystemRole:
      "Flawless X is the independent quality and operational excellence authority of the KOT ecosystem, ensuring that everything delivered by every subsidiary reflects the Group's commitment to excellence, security, trust, and continuous improvement.",
    capabilities: [
      { title: "Quality Engineering", desc: "Quality practices designed into software, platforms, products, and operations." },
      { title: "Software Testing", desc: "Functional, automation, performance, usability, accessibility, compatibility, security, and UAT." },
      { title: "Cybersecurity Assurance", desc: "Vulnerability assessment, penetration testing, maturity assessment, architecture review, resilience testing." },
      { title: "Governance, Risk & Compliance", desc: "Governance frameworks, enterprise risk management, compliance programs, internal controls." },
      { title: "Business Process Excellence", desc: "Lean optimization, process reengineering, automation, operational analytics." },
      { title: "Certification Readiness", desc: "Gap assessment, documentation, implementation support, and audit preparation for national and international certifications." },
      { title: "Enterprise Auditing", desc: "Quality, operational, technology, and cybersecurity audits." },
      { title: "Operational Resilience", desc: "Business continuity, disaster recovery, crisis management, preparedness." },
      { title: "Performance Intelligence", desc: "Quality metrics, scorecards, executive reporting, continuous monitoring." },
    ],
    details: {
      serve: {
        num: "08",
        title: "Industries We Serve",
        chips: [
          "Technology",
          "Government",
          "Healthcare",
          "Financial Services",
          "Education",
          "Manufacturing",
          "Energy",
          "Telecommunications",
          "Logistics",
          "Commerce",
          "Aviation",
          "Public Infrastructure",
          "Smart Cities",
        ],
      },
      expertise: {
        num: "09",
        title: "Standards & Expertise",
        chips: [
          "Test Automation & CI/CD Quality Gates",
          "Security Tooling & SOC Practices",
          "Risk Analytics & Process Mining",
          "Accessibility Standards",
          "Audit Methodology",
          "Resilience Engineering",
        ],
      },
      framework: {
        num: "10",
        title: "Our Framework — The Quality Excellence Framework",
        intro: "We work with internationally recognized quality, governance, security, and operational-excellence frameworks — helping organizations achieve certification readiness, describing alignment honestly and never overstating certification status.",
        steps: [
          { title: "Organizational Assessment", desc: "Governance, maturity, systems, security posture, risk." },
          { title: "Quality Strategy", desc: "Enterprise quality frameworks aligned to objectives and standards." },
          { title: "Process Excellence", desc: "Workflows, controls, automation, and measurement optimized." },
          { title: "Quality Engineering", desc: "Quality embedded across development, platforms, and operations." },
          { title: "Assurance & Compliance", desc: "Testing, auditing, security validation, certification readiness." },
          { title: "Continuous Improvement", desc: "Metrics, feedback, and incidents converted into progress." },
          { title: "Excellence Culture", desc: "Leadership, engagement, and accountability sustaining it all." },
        ],
      },
      philosophy: {
        num: "11",
        title: "Our AI Philosophy",
        text: "AI makes quality predictive: quality analytics, intelligent defect detection, automated compliance monitoring, risk prediction, process mining, security anomaly detection, AI-assisted auditing, and continuous control monitoring. AI strengthens expert judgment — under fairness, transparency, accountability, and privacy, always.",
      },
      reasons: {
        num: "12",
        title: "Why Choose Flawless X",
        items: [
          { lead: "Predictive, not reactive.", text: "Failures prevented, not just found." },
          { lead: "Whole-enterprise scope.", text: "Software, security, process, governance — one framework." },
          { lead: "Independent by mandate.", text: "We are the standards authority even inside our own group." },
          { lead: "Certification-serious.", text: "Structured readiness, honest claims, audit-grade documentation." },
          { lead: "Ecosystem-proven.", text: "Every KOT company ships through our gates first." },
        ],
      },
      ecosystem: {
        num: "13",
        title: "The KOT Ecosystem Advantage",
        text: "Flawless X is the independent quality authority across the KOT ecosystem: hardening Digital Systems' platforms, validating AppForge's user experience and accessibility, installing governance in Venture Xcelerator's startups, supporting EpiTech's accreditation work, securing Commerce X's transactions, assuring Platform Systems' reliability, and holding Visual Systems' production to standard. Clients get the same discipline we apply to our own group — which is the strongest reference we can offer.",
      },
      globalLocal: {
        num: "14",
        title: "Global & Local",
        local: "Locally, we raise the quality and security baseline of Pakistan's enterprises, public infrastructure, and institutions — including certification readiness for organizations entering regulated and export markets.",
        global: "Globally, we deliver international-standard testing, security assurance, and GRC services at a compelling cost base, for clients who need quality they can prove to their own regulators and customers.",
      },
      partner: {
        num: "15",
        title: "Partner With Us",
        cards: [
          { title: "Enterprises & institutions", desc: "Start with an Organizational Assessment; know exactly where you stand." },
          { title: "Technology companies", desc: "Testing, security, and quality engineering embedded in your delivery pipeline." },
          { title: "Government & regulators", desc: "Public-infrastructure assurance, audit capability, and resilience programs." },
          { title: "Investors", desc: "Assurance-services exposure inside a governed, SECP-regulated group. info@groupofcompanies.com" },
          { title: "Universities & professional institutes", desc: "Quality-engineering curriculum, certification training, and research." },
          { title: "Media & partners", desc: "info@groupofcompanies.com" },
        ],
        closing: "Flawless X — a company of KOT Enterprises · 58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan · info@groupofcompanies.com",
      },
    },
    about: {
      title: "Flawless X is the quality intelligence and operational excellence company of KOT Enterprises.",
      paragraphs: [
        "We help organizations achieve — and prove — the highest standards of quality, security, governance, compliance, and operational resilience.",
        "Created inside the KOT Enterprises group (founded in Lahore, 2018), Flawless X holds a distinctive mandate: everything the KOT ecosystem builds passes through our standards. We exist because quality treated as a final inspection always fails — and quality built in from the start always compounds.",
      ],
      leadership: {
        quote: "Trust is the real product every organization sells. Flawless X exists to make trust engineerable — measured, audited, secured, and continuously improved. Inside our own group first, and for every client who refuses to treat quality as an afterthought.",
        name: "Meeran Nasir",
        role: "Chief Executive Officer",
      },
      snapshot: [
        { label: "Company", value: "Flawless X — a company of KOT Enterprises" },
        { label: "Parent", value: "KOT Enterprises (Private) Limited, Lahore (est. 2018, SECP-regulated)" },
        { label: "Ownership", value: "Wholly owned by KOT Enterprises" },
        { label: "Industry", value: "Quality engineering · Cybersecurity assurance · GRC · Operational excellence" },
        { label: "Business Model", value: "Quality intelligence services — testing, security assurance, governance, audit, certification readiness" },
        { label: "Head Office", value: "58-A2 Tipu Road, Gulberg III, Lahore, Punjab, Pakistan" },
        { label: "Markets", value: "Pakistan, expanding regionally and internationally" },
        { label: "Website / Contact", value: "info@groupofcompanies.com · website pending" },
      ],
      sections: [
        {
          num: "01",
          title: "The Challenge We Solve",
          paragraphs: [
            "Organizations discover quality problems the expensive way: in production, in breach reports, in failed audits, in customers who quietly leave. Conventional QA — testing at the end, compliance as paperwork — catches symptoms after the damage is priced in.",
            "Flawless X moves quality upstream. We integrate quality engineering, cybersecurity assurance, governance, risk, compliance, and process excellence into one framework — turning quality from a cost center into a strategic capability that prevents failures instead of documenting them.",
          ],
        },
        {
          num: "02",
          title: "Who We Are",
          paragraphs: [
            "We are an enterprise-wide quality intelligence organization — well beyond a software testing firm. Our purpose is to transform quality into a capability that drives innovation, trust, and competitiveness: intelligent quality management, stronger governance, reduced operational risk, improved cybersecurity maturity, and certification readiness.",
            "Our vision: quality embedded in every organizational decision, process, technology, and customer experience.",
          ],
        },
        {
          num: "03",
          title: "Our Strategic Direction",
          paragraphs: [
            "We are moving organizations from reactive quality control to predictive quality intelligence — where data, analytics, automation, and continuous learning prevent failures before they happen and optimize performance permanently.",
          ],
        },
      ],
    },
  },
  {
    slug: "challenge-x",
    type: "program",
    name: "Challenge X",
    tagline: "The Global Challenge Intelligence Platform",
    headline: "Where real-world challenges meet the people who can solve them",
    summary:
      "Challenge X is a global, community-driven platform where anyone — individuals, organizations, governments — can publish a real-world challenge, and an AI-powered matching engine connects it with the people, technologies, and solutions most likely to solve it. It turns fragmented global problem-solving into a single, searchable, collaborative innovation ecosystem.",
    purpose: [
      "Let anyone worldwide publish real-world challenges",
      "Use AI to assess solution feasibility and prioritize solvable problems",
      "Match challenges with qualified solvers, technologies, and funding",
      "Accelerate open innovation, startup creation, and measurable impact",
    ],
    services: [
      { title: "Challenge Publishing", desc: "A global platform for individuals, organizations, and governments to list real-world challenges." },
      { title: "AI Feasibility Assessment", desc: "Intelligent filtering that prioritizes challenges with technologically feasible solutions." },
      { title: "Intelligent Matching", desc: "AI recommendations connecting challenges with collaborators, technologies, mentors, and funding." },
      { title: "Multi-Industry Coverage", desc: "Challenges organized across healthcare, agriculture, education, energy, smart cities, and more." },
      { title: "Persona-Based Discovery", desc: "Tailored opportunities for students, researchers, startups, corporations, investors, and governments." },
      { title: "Collaboration & Commercialization", desc: "Multidisciplinary teamwork that accelerates innovation and entrepreneurial problem-solving." },
    ],
    ecosystemRole:
      "Challenge X operates under the strategic leadership of Venture Xcelerator, with Digital Systems providing the AI and search infrastructure and support from Platform Systems, Growth IQ, and EpiTech.",
  },
  {
    slug: "platform-9-34",
    type: "program",
    name: "Platform 9¾",
    tagline: "The Immersive Themed Café Experience",
    headline: "A dining destination where storytelling meets flavour",
    summary:
      "Platform 9¾ is an immersive, themed café and dining destination built around storytelling, décor, and interactive dining. Guests craft their own dishes at a self-cook noodle bar or enjoy a parallel desi-continental menu, all within an ambiance inspired by a magical school and travel theme — a guest experience, not just a food outlet.",
    purpose: [
      "Create a destination dining experience led by storytelling and immersion",
      "Offer interactive, self-cook dining alongside Pakistani and continental cuisine",
      "Extend the experience through merchandise, themed events, and seasonal offerings",
      "Develop an original, legally sound thematic identity",
    ],
    services: [
      { title: "Self-Cook Noodle Bar", desc: "Guests prepare their own noodle dishes from curated broths, proteins, and toppings." },
      { title: "Desi-Continental Dining", desc: "A parallel menu of Pakistani and international dishes." },
      { title: "Immersive Themed Interior", desc: "Ambiance, staff experience, and guest journey inspired by a magical school and travel theme." },
      { title: "Merchandise & Themed Events", desc: "Experiential retail, seasonal offerings, and special events that extend the story." },
    ],
    ecosystemRole:
      "Platform 9¾ operates under the strategic leadership of Visual Systems for creative concept and guest experience design, with Commerce X running hospitality commerce, Growth IQ leading branding and marketing, and Flawless X ensuring quality and intellectual property compliance.",
  },
  {
    slug: "madad-farma",
    type: "program",
    name: "Madad Farma",
    tagline: "Community Welfare & Human Capital Initiative",
    headline: "Restoring dignity, capability, and opportunity",
    summary:
      "Madad Farma is KOT Enterprises' flagship community welfare initiative, serving Pakistan's white-collar and middle-income families who earn a regular income yet struggle to meet basic needs — a population conventional welfare overlooks. Through free food, education, healthcare, and technology, it restores dignity, capability, and opportunity to underserved communities.",
    purpose: [
      "Provide free, dignified access to food for eligible families",
      "Deliver free education and employment pathways to out-of-school children and youth",
      "Extend medical services and health capacity to underserved communities",
      "Amplify and measure impact through technology integration",
    ],
    services: [
      { title: "Free Food Centres", desc: "Free, nutritious food delivered in a way that preserves recipients' dignity and privacy." },
      { title: "Free Education & Employment Centers", desc: "Quality education focused on mindset transformation, with structured pathways into employment." },
      { title: "Medical & Health Services", desc: "Medical services, health supplies, and capacity-building support for public health institutions." },
      { title: "Technology Integration (Fourth Pillar)", desc: "A reserved pillar using technology to extend, measure, and amplify the impact of the first three." },
      { title: "International Workforce Development", desc: "Training, certification, and language preparation for ethical overseas employment opportunities." },
      { title: "University Collaboration", desc: "Partnerships with universities and incubation centers, from NUST to the University of Chitral." },
    ],
    ecosystemRole:
      "Madad Farma is a joint strategic initiative of EpiTech and Venture Xcelerator, operating under their shared oversight with an appointed Executive Director — the KOT ecosystem's flagship expression of social impact, starting across Pakistan.",
  },
  {
    slug: "her-x",
    type: "program",
    name: "Her X",
    tagline: "Women Leadership & Enterprise Initiative",
    headline: "Empowering women to lead, build, and thrive",
    summary:
      "Her X is KOT Enterprises' official women leadership, entrepreneurship, and empowerment initiative. It creates an inclusive ecosystem that supports women in building businesses, advancing careers, acquiring skills, and strengthening leadership — driving women's participation across entrepreneurship, technology, finance, and community development.",
    purpose: [
      "Support women-owned businesses and female entrepreneurship",
      "Develop leadership capabilities and digital skills",
      "Improve financial literacy and access to investment",
      "Encourage women's participation in STEM and promote economic inclusion",
    ],
    services: [
      { title: "Women Entrepreneur Network", desc: "A community connecting women founders and business owners." },
      { title: "Women Startup Accelerator", desc: "Structured acceleration support for women-led startups." },
      { title: "Leadership Academy", desc: "Leadership development programs for women professionals and executives." },
      { title: "Digital Skills Academy", desc: "Training that strengthens digital and technology capabilities." },
      { title: "Women Investment Forum", desc: "Connecting women entrepreneurs with funding and investment opportunities." },
      { title: "Annual Her X Summit & Innovation Awards", desc: "A flagship gathering with mentorship, certification programs, and recognition of women innovators." },
    ],
    ecosystemRole:
      "Her X is a flagship strategic program of KOT Enterprises, collaborating with government agencies, financial institutions, universities, chambers of commerce, women's business associations, and international development partners across the ecosystem.",
  },
  {
    slug: "saqafat",
    type: "program",
    name: "Saqafat",
    tagline: "Culture, Ethics & Creative Economy Initiative",
    headline: "Stories, ethics, and culture for the modern workplace",
    summary:
      "Saqafat is KOT Enterprises' official podcast and cultural initiative, exploring the culture, ethics, and human dynamics of educational institutions and workplaces. Delivered primarily as a podcast series extending into digital platforms and multimedia, it preserves cultural identity while building sustainable creative-economy opportunities through authentic storytelling.",
    purpose: [
      "Strengthen workplace and institutional culture through ethics and connection",
      "Promote Pakistan's cultural heritage through modern digital media",
      "Foster entrepreneurship, intellectual growth, and ethical leadership",
      "Build sustainable revenue through podcasts, subscriptions, and licensing",
    ],
    services: [
      { title: "Ikhlaqiat — Ethics That Empower", desc: "Identifying moral-ethics gaps in workplaces and institutions and exploring practical solutions." },
      { title: "Hayya 'Alal Falah — Business Coaching", desc: "Entrepreneurship education, leadership development, and startup mentoring for sustainable growth." },
      { title: "Rabta — Connected People", desc: "Strengthening connection, communication, and social cohesion in workplaces and institutions." },
      { title: "Idraak — Mind Nourishment", desc: "Building intellectual curiosity and confidence to close the gap between leading and following minds." },
    ],
    ecosystemRole:
      "Saqafat is a flagship strategic program of KOT Enterprises that carries the Group's values into culture and media, developing podcasts, digital platforms, and creative content for domestic and international audiences with sustainable monetization models.",
  },
];

export const getEntity = (slug: string): Entity | undefined =>
  entities.find((e) => e.slug === slug);
